import {
  CellTemplateProp,
  ColumnData,
  ColumnDataSchemaModel,
  ColumnRegular,
  DataType,
  HyperFunc,
  VNode,
} from '@revolist/react-datagrid';

import {
  RevoGridTableExtras,
  SelectOption,
  SelectOptionsSource,
  createSelectCellTemplate,
  createSelectEditor,
  createToggleCellTemplate,
  editButtonCellTemplate,
  resolveSelectOptions,
  withCellTestIds,
} from 'src/components/streametric/revoGridTable';
import { ALARM_STATE } from 'src/hooks/featureHooks/useTagTableState/types/alarmState';
import { TAG_TYPE } from 'src/hooks/featureHooks/useTagTableState/types/tagType';
import { DataSourceTypeEnum } from 'src/types/data-sources/types';
import { GatewayOptionsEnum, GatewayOptionsTypes } from 'src/types/gateways';

import {
  resolveDataTypeOptions,
  resolveRawUnitOptions,
} from '../components/EditTagModalFormV2/editTagModalFormV2Rules';
import dataTypes from '../data/data-types.json';
import { isAutoAlarmRulesEnabled } from './autoAlarmRules';

export type GatewayTagColumn = ColumnRegular;

export const TAG_TYPE_OPTIONS: SelectOption[] = dataTypes;

const TAG_TYPE_OPTIONS_WITH_BLANK: SelectOption[] = [{ label: '', value: '' }, ...TAG_TYPE_OPTIONS];
/** Built once at module scope: `columns` has to stay a stable reference. */
const tagTypeAutocompleteEditor = createSelectEditor(TAG_TYPE_OPTIONS_WITH_BLANK, {
  autocomplete: true,
});
const tagTypeEditorForCalculated = createSelectEditor([
  { label: 'Calculated', value: 'calculated' },
]);

const withBlankOption =
  (source: SelectOptionsSource): SelectOptionsSource =>
  (model) => {
    const resolved = resolveSelectOptions(source, model);
    return resolved.length === 0 ? resolved : [{ label: '', value: '' }, ...resolved];
  };

const dataTypeOptionsWithBlank = withBlankOption(resolveDataTypeOptions);

const rawUnitAutocompleteEditor = createSelectEditor(resolveRawUnitOptions, {
  autocomplete: true,
});
const dataTypeOfValuesEditor = createSelectEditor(dataTypeOptionsWithBlank);

/** Visibility is a select column with Hidden and Visible which translates to the row's hidden boolean */
export const VISIBILITY_OPTIONS: SelectOption[] = [
  { label: 'Visible', value: 'Visible' },
  { label: 'Hidden', value: 'Hidden' },
];
const visibilityEditor = createSelectEditor(VISIBILITY_OPTIONS);

const isAlarmRow = (model?: DataType): boolean => model?.dataType === TAG_TYPE.ALARM;

const autoAlarmCellTemplate = createToggleCellTemplate({
  name: 'auto-alarm',
  isApplicable: isAlarmRow,
});

/** The raw value the generated alarm rule fires on. No blank option: an Alarm tag always has one. */
const ALARM_STATE_OPTIONS: SelectOption[] = [
  { label: ALARM_STATE.ZERO, value: ALARM_STATE.ZERO },
  { label: ALARM_STATE.ONE, value: ALARM_STATE.ONE },
];

const resolveAlarmStateOptions = (model: DataType): readonly SelectOption[] =>
  isAlarmRow(model) ? ALARM_STATE_OPTIONS : [];

const alarmStateEditor = createSelectEditor(resolveAlarmStateOptions);
const alarmStateSelectCellTemplate = createSelectCellTemplate(resolveAlarmStateOptions);

const alarmStateCellTemplate = (
  h: HyperFunc<VNode>,
  props: CellTemplateProp,
  extra?: RevoGridTableExtras
) => (isAlarmRow(props.model) ? alarmStateSelectCellTemplate(h, props, extra) : h('span'));

/**
 * Gated as a pair with the rule creation itself (`useCreateAutoAlarmRules`):
 * with the flag off neither column is in the head, so there is nothing to set
 * and no rule gets generated on save.
 */
const ALARM_RULE_TAG_COLUMNS: ColumnRegular[] = isAutoAlarmRulesEnabled
  ? [
      {
        prop: 'autoAlarm',
        name: 'Auto Alarm',
        size: 160,
        readonly: true,
        cellTemplate: autoAlarmCellTemplate,
      },
      {
        prop: 'alarmState',
        name: 'Alarm State',
        size: 180,
        readonly: ({ model }: ColumnDataSchemaModel) => !isAlarmRow(model),
        editor: alarmStateEditor,
        cellTemplate: alarmStateCellTemplate,
      },
    ]
  : [];

export const RAW_INPUT_TAG_TABLE_COLUMNS: ColumnData = withCellTestIds([
  {
    name: '',
    children: [
      { prop: 'rawInputName', name: 'Tag Name', size: 200, readonly: true },
      {
        prop: 'dataType',
        name: 'Tag Type',
        size: 180,
        editor: tagTypeAutocompleteEditor,
        cellTemplate: createSelectCellTemplate(TAG_TYPE_OPTIONS_WITH_BLANK, { autocomplete: true }),
      },
      { prop: 'displayName', name: 'Tag Display Name', size: 200 },
      {
        prop: 'dataTypeOfValues',
        name: 'Data Type',
        size: 140,
        editor: dataTypeOfValuesEditor,
        cellTemplate: createSelectCellTemplate(dataTypeOptionsWithBlank),
      },
      {
        prop: 'rawUnit',
        name: 'Unit',
        size: 180,
        editor: rawUnitAutocompleteEditor,
        cellTemplate: createSelectCellTemplate(resolveRawUnitOptions, { autocomplete: true }),
      },
    ],
  },
  {
    name: 'Advanced',
    children: [
      { prop: 'displayAs', name: 'Display As', size: 160, readonly: true },
      { prop: 'controllable', name: 'Controllable', size: 160, readonly: true },
      { prop: 'controller', name: 'Controller', size: 160, readonly: true },
      { prop: 'acceptableRangeValue', name: 'Acceptable Range/Value', size: 250, readonly: true },
      ...ALARM_RULE_TAG_COLUMNS,
      {
        prop: 'visibility',
        name: 'Visibility',
        size: 160,
        editor: visibilityEditor,
        cellTemplate: createSelectCellTemplate(VISIBILITY_OPTIONS),
      },
    ],
  },
  {
    prop: 'edit',
    name: '',
    size: 56,
    readonly: true,
    cellTemplate: editButtonCellTemplate,
    sortable: false,
    filter: false,
  },
]);

export const BASIC_TAG_TABLE_COLUMNS: ColumnData = withCellTestIds([
  { prop: 'rawInputName', name: 'Raw Input Names', size: 400, readonly: true },
  {
    prop: 'dataType',
    name: 'Tag Type',
    size: 250,
    editor: tagTypeEditorForCalculated,
    cellTemplate: createSelectCellTemplate([{ label: 'Calculated', value: 'calculated' }]),
  },
  { prop: 'displayName', name: 'Tag Display Name', size: 400 },
  {
    prop: 'visibility',
    name: 'Visibility',
    size: 160,
    editor: visibilityEditor,
    cellTemplate: createSelectCellTemplate(VISIBILITY_OPTIONS),
  },
]);

export const AI_FLOW_TAG_TABLE_COLUMNS: ColumnData = withCellTestIds([
  { prop: 'rawInputName', name: 'Raw Input Names', size: 200, readonly: true },
  {
    prop: 'dataType',
    name: 'Tag Type',
    size: 180,
    editor: tagTypeAutocompleteEditor,
    cellTemplate: createSelectCellTemplate(TAG_TYPE_OPTIONS_WITH_BLANK, { autocomplete: true }),
  },
  {
    prop: 'rawUnit',
    name: 'Raw Unit',
    size: 180,
    editor: rawUnitAutocompleteEditor,
    cellTemplate: createSelectCellTemplate(resolveRawUnitOptions, { autocomplete: true }),
  },
  { prop: 'displayName', name: 'Tag Display Name', size: 200 },
  {
    prop: 'visibility',
    name: 'Visibility',
    size: 160,
    editor: visibilityEditor,
    cellTemplate: createSelectCellTemplate(VISIBILITY_OPTIONS),
  },
]);

export const WEATHER_TAG_TABLE_COLUMNS: ColumnData = withCellTestIds([
  { prop: 'rawInputName', name: 'Raw Input Names', size: 200, readonly: true },
  { prop: 'dataType', name: 'Tag Type', size: 180, readonly: true },
  { prop: 'rawUnit', name: 'Raw Unit', size: 180, readonly: true },
  { prop: 'displayName', name: 'Tag Display Name', size: 200 },
  {
    prop: 'visibility',
    name: 'Visibility',
    size: 160,
    editor: visibilityEditor,
    cellTemplate: createSelectCellTemplate(VISIBILITY_OPTIONS),
  },
]);

export const DATA_SOURCE_TAG_TABLE_COLUMNS: Partial<Record<DataSourceTypeEnum, ColumnData>> = {
  [DataSourceTypeEnum.ASSET_PROPERTIES]: BASIC_TAG_TABLE_COLUMNS,
  [DataSourceTypeEnum.STREAMETRIC_AI]: AI_FLOW_TAG_TABLE_COLUMNS,
  [DataSourceTypeEnum.LOGS]: AI_FLOW_TAG_TABLE_COLUMNS,
  [DataSourceTypeEnum.WEATHER_API]: WEATHER_TAG_TABLE_COLUMNS,
  [DataSourceTypeEnum.INTEGRALERT]: RAW_INPUT_TAG_TABLE_COLUMNS,
};

export const getDataSourceTagTableColumns = (type?: DataSourceTypeEnum | null): ColumnData =>
  (type && DATA_SOURCE_TAG_TABLE_COLUMNS[type]) || [];

export const GATEWAY_TAG_TABLE_COLUMNS: Partial<Record<GatewayOptionsTypes, ColumnData>> = {
  [GatewayOptionsEnum.StreamLite]: RAW_INPUT_TAG_TABLE_COLUMNS,
  [GatewayOptionsEnum.StreamMLitePayload]: RAW_INPUT_TAG_TABLE_COLUMNS,
  [GatewayOptionsEnum.Inhand]: RAW_INPUT_TAG_TABLE_COLUMNS,
  [GatewayOptionsEnum.FX30]: RAW_INPUT_TAG_TABLE_COLUMNS,
  [GatewayOptionsEnum.Ewon]: AI_FLOW_TAG_TABLE_COLUMNS,
  [GatewayOptionsEnum.Kallipr]: AI_FLOW_TAG_TABLE_COLUMNS,
};

export const getGatewayTagTableColumns = (gatewayType?: GatewayOptionsTypes): ColumnData =>
  (gatewayType && GATEWAY_TAG_TABLE_COLUMNS[gatewayType]) || [];
